/**
 * Client-side interface for the inking Web Worker.
 *
 * Provides a singleton worker plus an abortable, progress-aware async call.
 * The worker runs the pipeline in phases and posts progress messages between
 * them; `AbortSignal` triggers a cancel dispatch that the worker honours at
 * the next phase boundary.
 */

import { createSingletonWorker } from "../../../workers/createWorkerRPC.ts";
import { recycleGBuffer } from "./gbufferPool.ts";
import type {
  InkingProgressEvent,
  InkingWorkerRequest,
  InkingWorkerResponse,
} from "./inking.worker.ts";
import type {
  GBufferData,
  InkingPipelineParams,
  InkingPipelineResult,
} from "./types.ts";

const getWorker = createSingletonWorker(
  () =>
    new Worker(new URL("./inking.worker.ts", import.meta.url), {
      type: "module",
    }),
);

let _nextId = 1;

export interface InkingBakeOptions {
  /** Called on every phase transition (0…1). */
  onProgress?: (ev: InkingProgressEvent) => void;
  /** Cancel the bake cooperatively at the next phase boundary. */
  signal?: AbortSignal;
  /**
   * Stall warning threshold (ms). When exceeded, a warning toast is shown.
   * Matches the existing `stallWarningMs` semantics. Pass `0` to disable.
   */
  stallWarningMs?: number;
  /** Custom stall warning message. */
  stallMessage?: string;
}

/**
 * Custom error thrown when a bake is cancelled via `options.signal`. Lets
 * callers distinguish expected cancellations from real failures.
 */
export class InkingBakeCancelledError extends Error {
  constructor() {
    super("Inking bake cancelled");
    this.name = "InkingBakeCancelledError";
  }
}

/**
 * Run the full CPU inking pipeline in the Web Worker.
 *
 * G-buffer typed-array buffers are transferred (zero-copy); the caller must
 * not access them after this call returns.
 */
export async function runInkingPipelineInWorker(
  gbuffer: GBufferData,
  params: InkingPipelineParams,
  options: InkingBakeOptions = {},
): Promise<InkingPipelineResult> {
  const worker = getWorker();
  const rpcId = _nextId++;

  const transferables: Transferable[] = [
    gbuffer.depth.buffer,
    gbuffer.normals.buffer,
    gbuffer.objectIds.buffer,
    gbuffer.luminance.buffer,
    gbuffer.resolvedAutoSmoothAngles.buffer,
  ];

  const request: InkingWorkerRequest = {
    kind: "run",
    _rpcId: rpcId,
    gbuffer,
    params,
  };

  return new Promise<InkingPipelineResult>((resolve, reject) => {
    let stallTimer: ReturnType<typeof setTimeout> | null = null;
    let signalListener: (() => void) | null = null;

    const cleanup = () => {
      worker.removeEventListener("message", onMessage);
      worker.removeEventListener("error", onError);
      if (stallTimer) {
        clearTimeout(stallTimer);
        stallTimer = null;
      }
      if (signalListener && options.signal) {
        options.signal.removeEventListener("abort", signalListener);
        signalListener = null;
      }
    };

    const onMessage = (e: MessageEvent<InkingWorkerResponse>) => {
      const m = e.data;
      if (!m || m._rpcId !== rpcId) return;
      if (m.kind === "progress") {
        options.onProgress?.(m);
        return;
      }
      recycleGBuffer(m.recycledGbuffer);
      if (m.kind === "cancelled") {
        cleanup();
        reject(new InkingBakeCancelledError());
        return;
      }
      if (m.kind === "done") {
        cleanup();
        if (m.ok) resolve(m.result);
        else reject(new Error(m.error ?? "Worker error"));
        return;
      }
    };

    const onError = (e: ErrorEvent) => {
      cleanup();
      reject(new Error(e.message ?? "Worker error"));
    };

    worker.addEventListener("message", onMessage);
    worker.addEventListener("error", onError);

    if (options.stallWarningMs && options.stallWarningMs > 0) {
      const stallMessage =
        options.stallMessage ??
        "Inking pipeline is taking longer than expected.";
      stallTimer = setTimeout(() => {
        import("../../../ui/toastBus.ts")
          .then(({ showToast }) => {
            showToast(stallMessage, {
              severity: "warning",
              duration: 6000,
              dedupKey: "inking-bake",
            });
          })
          .catch(() => {});
      }, options.stallWarningMs);
    }

    if (options.signal) {
      if (options.signal.aborted) {
        worker.postMessage({
          kind: "cancel",
          _rpcId: rpcId,
        } satisfies InkingWorkerRequest);
      } else {
        signalListener = () => {
          worker.postMessage({
            kind: "cancel",
            _rpcId: rpcId,
          } satisfies InkingWorkerRequest);
        };
        options.signal.addEventListener("abort", signalListener);
      }
    }

    worker.postMessage(request, transferables);
  });
}
